/**
 * Roofing V4 product selection.
 *
 * A new development project may start with the embedded catalogue's synthetic
 * complete-system fixture so the application can demonstrate a full result and
 * membrane plan immediately. That catalogue-backed demo state is kept distinct
 * from both the source-backed EJOT ETA component path and the explicit
 * user-defined tested-system override.
 */

import { useQuery } from '@tanstack/react-query';

import { catalogueService } from '../../api/client';
import { ETAReferenceBrowser } from './ETAReferenceBrowser';
import { Field, FieldRow, SectionCard, SelectField } from '../../shared/components';
import type {
  CompatibilityContext,
  ETAReferenceSelection,
  FixingResistanceBasis,
  SubstrateCategory,
} from '../../gen/ejot/roofing/v1/types';

const COMPLETE_SYSTEM_BASIS: FixingResistanceBasis = 'FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN';
const ETA_COMPONENT_BASIS: FixingResistanceBasis = 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';

const SUBSTRATE_OPTIONS: readonly { value: SubstrateCategory; label: string }[] = [
  { value: 'SUBSTRATE_CATEGORY_STEEL_DECK', label: 'Steel deck' },
  { value: 'SUBSTRATE_CATEGORY_CONCRETE', label: 'Concrete' },
  { value: 'SUBSTRATE_CATEGORY_TIMBER', label: 'Timber' },
  { value: 'SUBSTRATE_CATEGORY_BOARD', label: 'Board' },
];

export type CompleteSystemSource = 'CATALOGUE' | 'USER_DEFINED';

export interface CatalogueSelectionState {
  substrateCategory?: SubstrateCategory;
  membraneProductId?: string;
  fixingCombinationId?: string;
  totalInsulationThicknessMm?: number;
  etaReference?: ETAReferenceSelection;
  fixingBasis?: FixingResistanceBasis;
  completeSystemSource?: CompleteSystemSource;
  userDefinedMembraneBrand?: string;
  userDefinedMembraneType?: string;
  userDefinedMembraneThicknessMm?: number;
  userDefinedFixingSystemName?: string;
  userDefinedCharacteristicResistanceKn?: number;
}

interface CatalogueSelectionProps {
  selection: CatalogueSelectionState;
  onChange: (selection: CatalogueSelectionState) => void;
}

export function CatalogueSelection({ selection, onChange }: CatalogueSelectionProps) {
  const userDefined = selection.fixingBasis === COMPLETE_SYSTEM_BASIS
    && selection.completeSystemSource === 'USER_DEFINED';
  const catalogueCompleteSystem = selection.fixingBasis === COMPLETE_SYSTEM_BASIS
    && selection.completeSystemSource === 'CATALOGUE';
  const context: CompatibilityContext = {
    substrate_category: selection.substrateCategory,
    total_insulation_thickness: selection.totalInsulationThicknessMm !== undefined
      ? { millimetres: selection.totalInsulationThicknessMm }
      : undefined,
  };
  const membranes = useQuery({
    queryKey: ['catalogue-membranes-v4', context],
    queryFn: ({ signal }) => catalogueService.listCompatibleMembranes({ context }, signal),
    enabled: !userDefined,
  });
  const fixingContext: CompatibilityContext = {
    ...context,
    membrane_product_id: selection.membraneProductId,
  };
  const fixingSystems = useQuery({
    queryKey: ['catalogue-fixing-systems-v4', fixingContext],
    queryFn: ({ signal }) => catalogueService.listCompatibleFixingSystems({ context: fixingContext }, signal),
    enabled: catalogueCompleteSystem && Boolean(selection.membraneProductId),
  });
  const membraneOptions = (membranes.data?.membranes ?? []).map((membrane) => ({
    value: membrane.id ?? '',
    label: membrane.display_name ?? membrane.id ?? '',
  }));
  const selectedMembrane = (membranes.data?.membranes ?? [])
    .find((membrane) => membrane.id === selection.membraneProductId);
  const selectedCombination = (fixingSystems.data?.combinations ?? [])
    .find((combination) => combination.id === selection.fixingCombinationId);
  const developmentDemo = Boolean(selectedMembrane?.development_only || selectedCombination?.development_only);
  let basisLabel = 'ETA pull-out';
  if (userDefined) basisLabel = 'User defined';
  if (catalogueCompleteSystem) {
    basisLabel = developmentDemo ? 'Development demo complete system' : 'Catalogue complete system';
  }

  const setUserDefined = (checked: boolean) => {
    if (checked) {
      onChange({
        ...selection,
        fixingBasis: COMPLETE_SYSTEM_BASIS,
        completeSystemSource: 'USER_DEFINED',
        membraneProductId: undefined,
        fixingCombinationId: undefined,
        etaReference: undefined,
        userDefinedMembraneBrand: selection.userDefinedMembraneBrand ?? '',
        userDefinedMembraneType: selection.userDefinedMembraneType ?? '',
        userDefinedMembraneThicknessMm: selection.userDefinedMembraneThicknessMm ?? 0,
        userDefinedFixingSystemName: selection.userDefinedFixingSystemName ?? '',
        userDefinedCharacteristicResistanceKn: selection.userDefinedCharacteristicResistanceKn ?? 0,
      });
      return;
    }

    onChange({
      ...selection,
      fixingBasis: ETA_COMPONENT_BASIS,
      completeSystemSource: undefined,
      fixingCombinationId: undefined,
      etaReference: undefined,
    });
  };

  const useETAComponents = () => onChange({
    ...selection,
    fixingBasis: ETA_COMPONENT_BASIS,
    completeSystemSource: undefined,
    membraneProductId: undefined,
    fixingCombinationId: undefined,
    etaReference: undefined,
  });

  return (
    <>
      <SectionCard title="Products" defaultOpen>
        {catalogueCompleteSystem ? (
          <>
            <Field
              label="Membrane"
              value={selectedMembrane?.display_name ?? selection.membraneProductId ?? ''}
              derived
            />
            <SelectField
              label="Substrate"
              value={selection.substrateCategory ?? ''}
              options={SUBSTRATE_OPTIONS}
            />
            <Field
              label="Fixing system"
              value={selectedCombination?.display_name ?? selection.fixingCombinationId ?? ''}
              derived
            />
          </>
        ) : (
          <>
            <SelectField
              label="Membrane"
              value={selection.membraneProductId ?? ''}
              options={membraneOptions}
              placeholder={membranes.isLoading ? 'Loading' : 'Select membrane'}
              onChange={(value) => onChange({ ...selection, membraneProductId: value || undefined })}
            />
            <SelectField
              label="Substrate"
              value={selection.substrateCategory ?? ''}
              options={SUBSTRATE_OPTIONS}
              placeholder="Select a substrate"
              onChange={(value) => onChange({
                ...selection,
                substrateCategory: (value || undefined) as SubstrateCategory | undefined,
                etaReference: value === selection.substrateCategory ? selection.etaReference : undefined,
              })}
            />
          </>
        )}

        <div className="catalogue-resistance-status" role="status">
          <span className="catalogue-resistance-status-label">Resistance basis</span>
          <strong>{basisLabel}</strong>
        </div>

        {catalogueCompleteSystem && (
          <div className="catalogue-demo-system-note" role="note">
            <p className="field-note">
              {developmentDemo
                ? 'Development-only demonstration system. It exists so a new project can show complete wind, fixing and membrane-layout outputs; it is not approved for design or installation.'
                : 'Catalogue complete-system selection. Product validity and resistance remain backend-authoritative.'}
            </p>
            <button type="button" className="text-button" onClick={useETAComponents}>
              Choose ETA products instead
            </button>
          </div>
        )}

        <label className="checkbox-row catalogue-user-defined-toggle">
          <input
            type="checkbox"
            checked={userDefined}
            onChange={(event) => setUserDefined(event.target.checked)}
          />
          <span>Use User Defined System Resistance</span>
        </label>
      </SectionCard>

      {userDefined ? (
        <>
          <SectionCard title="User Defined membrane">
            <Field
              label="Membrane brand"
              value={selection.userDefinedMembraneBrand ?? ''}
              onChange={(value) => onChange({ ...selection, userDefinedMembraneBrand: value })}
            />
            <FieldRow>
              <Field
                label="Membrane type"
                value={selection.userDefinedMembraneType ?? ''}
                onChange={(value) => onChange({ ...selection, userDefinedMembraneType: value })}
              />
              <Field
                label="Thickness"
                unit="mm"
                type="number"
                value={numberValue(selection.userDefinedMembraneThicknessMm)}
                onChange={(value) => onChange({ ...selection, userDefinedMembraneThicknessMm: finiteNumber(value) })}
              />
            </FieldRow>
          </SectionCard>

          <SectionCard title="User Defined tested system">
            <Field
              label="Fixing system"
              value={selection.userDefinedFixingSystemName ?? ''}
              onChange={(value) => onChange({ ...selection, userDefinedFixingSystemName: value })}
            />
            <Field
              label="Tested characteristic resistance"
              symbol="N_R,k"
              unit="kN"
              type="number"
              value={numberValue(selection.userDefinedCharacteristicResistanceKn)}
              onChange={(value) => onChange({
                ...selection,
                userDefinedCharacteristicResistanceKn: finiteNumber(value),
              })}
            />
            <p className="field-note">Use this only when a traceable test value exists for the selected membrane/fixing combination.</p>
          </SectionCard>
        </>
      ) : catalogueCompleteSystem ? null : (
        <ETAReferenceBrowser
          selection={selection.etaReference}
          calculationActive
          onChange={(etaReference) => onChange({
            ...selection,
            fixingBasis: ETA_COMPONENT_BASIS,
            completeSystemSource: undefined,
            etaReference,
          })}
        />
      )}
    </>
  );
}

function finiteNumber(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function numberValue(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value) ? '' : String(value);
}
